import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { cssVariables } from "@/utils/utils";
import { Button, Card, Checkbox, Col, Flex, Modal, Popconfirm, Row, Space, Tooltip } from "antd";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetDashboardWidgetSettings,
  useGetDefaultDashboardWidgetSettings,
  useUpdateDashboardWidgetSettings,
} from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { DashboardWidgetType } from "../../enums";
import { DashboardWidgetSettings, UpdateDashboardWidgetSettings } from "../../types";
import { DASHBOARD_LAYOUT_COLS, isDashboardWidgetTypeWithoutSettings, setDefaultLayoutSettings } from "../../utils";
import { DashboardResponsiveGrid } from "../DashboardResponsiveGrid";

type WidgetTypeWrapperProps = {
  type: DashboardWidgetType;
  isStatic: boolean;
};

const WidgetTypeWrapper = ({ isStatic, type }: WidgetTypeWrapperProps) => {
  const { t } = useTranslation();

  const staticStyles: React.CSSProperties = isStatic ? { cursor: "not-allowed", color: cssVariables.colorShadow } : {};

  return (
    <Card
      styles={{
        body: { padding: 6, height: "100%", cursor: "pointer", userSelect: "none", ...staticStyles },
      }}
      style={{ backgroundColor: isStatic ? cssVariables.colorHover : undefined, height: "100%" }}
    >
      <Tooltip title={isStatic ? t("dashboard.settings.helpers.notReplaceable") : undefined}>
        <Flex justify="center" align="center" className="center-align" style={{ height: "100%" }}>
          {t(`dashboard.enums.dashboardWidgetType.${type}`)}
        </Flex>
      </Tooltip>
    </Card>
  );
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
};

export const DashboardSettingsModal = ({ isOpen, onCancel }: Props) => {
  const { t } = useTranslation();

  const [editedLayouts, setEditedLayouts] = useState<DashboardWidgetSettings[]>();
  const [isLayoutTouched, setIsLayoutTouched] = useState(false);

  const { data } = useGetDashboardWidgetSettings();
  const updateDashboardWidgetSettings = useUpdateDashboardWidgetSettings();
  const getDefaultDashboardWidgetSettings = useGetDefaultDashboardWidgetSettings();

  const savedLayouts = data?.filter((item) => item.available) ?? [];
  const layouts = editedLayouts ?? savedLayouts;

  const handleModalSave = () => {
    updateDashboardWidgetSettings.mutate(layouts);
    onCancel();
  };

  const gridLayouts = layouts
    .filter((item) => item.enabled)
    .filter((item) => !isDashboardWidgetTypeWithoutSettings(item.widgetType));

  return (
    <Modal
      width={ModalSizes.LARGE}
      title={t("dashboard.settings.titles.main")}
      open={isOpen}
      onCancel={onCancel}
      mask={{ closable: false }}
      afterClose={() => {
        setEditedLayouts(undefined);
        setIsLayoutTouched(false);
      }}
      destroyOnHidden
      footer={
        <Flex justify="space-between" className="margin-top-medium">
          <div>
            <Popconfirm
              title={t("dashboard.settings.titles.backToDefaultConfirm")}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="primary"
              onConfirm={async () => {
                const defaultData = await getDefaultDashboardWidgetSettings.mutateAsync();
                const prepareDefaultData = defaultData
                  .filter((item) => item.available)
                  .map<UpdateDashboardWidgetSettings>((item) => ({
                    ...item,
                    optimisticLockVersion:
                      data?.find((currentItem) => currentItem.widgetType === item.widgetType)?.optimisticLockVersion ??
                      0,
                  }));

                updateDashboardWidgetSettings.mutate(prepareDefaultData);
                onCancel();
              }}
            >
              <Button icon={<AntIcon type="reload" />}>{t("dashboard.settings.actions.backToDefault")}</Button>
            </Popconfirm>
          </div>

          <Space>
            <Button onClick={onCancel}>{t("common.cancel")}</Button>

            <Button type="primary" onClick={handleModalSave}>
              {t("common.save")}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Row gutter={24} style={{ maxHeight: "100%" }}>
        <Col span={16} className="right-align">
          <h3 className="margin-top-small margin-bottom-tiny left-align">{t("dashboard.settings.titles.position")}</h3>

          <Flex justify="end" className="margin-bottom-tiny" gap={10}>
            {layouts
              .filter((item) => item.enabled)
              .find((item) => item.widgetType === DashboardWidgetType.PERSONAL_DATES_EXTENDED) ? (
              <div style={{ height: 40, width: 250, textAlign: "right" }}>
                <WidgetTypeWrapper type={DashboardWidgetType.PERSONAL_DATES_EXTENDED} isStatic />
              </div>
            ) : undefined}

            {layouts.filter((item) => item.enabled).find((item) => item.widgetType === DashboardWidgetType.NOTICES) ? (
              <div style={{ height: 40, width: 100, textAlign: "right" }}>
                <WidgetTypeWrapper type={DashboardWidgetType.NOTICES} isStatic />
              </div>
            ) : undefined}
          </Flex>

          <Card>
            <DashboardResponsiveGrid
              onLayoutChange={(currentLayout) => {
                setEditedLayouts((prevLayouts) =>
                  (prevLayouts ?? savedLayouts).map((layout) => ({
                    ...layout,
                    layoutSettings: currentLayout.find((item) => item.i === layout.widgetType),
                  })),
                );

                setIsLayoutTouched(true);
              }}
              layouts={{
                lg: gridLayouts
                  .map((item) => item.layoutSettings ?? setDefaultLayoutSettings(item))
                  .filter((layoutSettings) => !!layoutSettings),
              }}
              cols={{ lg: DASHBOARD_LAYOUT_COLS }}
              rowHeight={40}
              breakpoints={{ lg: 1500 }}
              resizeConfig={{ enabled: false }}
            >
              {gridLayouts.map((widget) => (
                <div key={widget.widgetType} style={{ height: "100%" }}>
                  <WidgetTypeWrapper type={widget.widgetType} isStatic={!!widget.layoutSettings?.static} />
                </div>
              ))}
            </DashboardResponsiveGrid>
          </Card>

          {isLayoutTouched ? (
            <ActionButton className="margin-top-small" icon="reload" onClick={() => setEditedLayouts(undefined)}>
              {t("dashboard.settings.actions.backChanges")}
            </ActionButton>
          ) : undefined}
        </Col>
        <Col span={8} style={{ maxHeight: "100%" }}>
          <h3 className="margin-top-small margin-bottom-small">{t("dashboard.settings.titles.availableWidgets")}</h3>

          <Checkbox.Group
            onChange={(enabledWidgets) => {
              setEditedLayouts((prevLayouts) =>
                (prevLayouts ?? savedLayouts).map((item) => {
                  const isEnabled = enabledWidgets.includes(item.widgetType);

                  if (isDashboardWidgetTypeWithoutSettings(item.widgetType)) {
                    return {
                      ...item,
                      enabled: isEnabled,
                    };
                  }

                  return {
                    ...item,
                    enabled: isEnabled,
                    layoutSettings: isEnabled ? (item.layoutSettings ?? setDefaultLayoutSettings(item)) : undefined,
                  };
                }),
              );
            }}
            options={layouts.map((item) => ({
              label: (
                <Tooltip title={item.layoutSettings?.static ? t("dashboard.settings.helpers.notEditable") : undefined}>
                  {t(`dashboard.enums.dashboardWidgetType.${item.widgetType}`)}
                </Tooltip>
              ),
              value: item.widgetType,
              style: { display: "flex", width: "100%", padding: "8px 0" },
              disabled: item.layoutSettings?.static,
            }))}
            value={layouts.filter((item) => item.enabled).map((item) => item.widgetType)}
          />
        </Col>
      </Row>
    </Modal>
  );
};
